import React, { useMemo, useState } from "react";
import {
  DEMO_BOOKS,
  DEMO_KULIAH_PRESETS,
  DEMO_SPEAKERS,
} from "../../features/demo/demoCatalog";
import {
  demoKuliahStartMillis,
  formatMalaysiaDate,
  validateDemoKuliah,
} from "../../features/demo/demoContract";
import DemoSpeakerSelect from "./DemoSpeakerSelect";
import EtikafSelect from "../EtikafSelect";

const scheduleFor = (preset, now = new Date()) => {
  let date = formatMalaysiaDate(now);
  let candidateMillis = demoKuliahStartMillis({ date, startTime: preset.time });
  if (candidateMillis - now.getTime() < 30 * 60 * 1000) {
    date = formatMalaysiaDate(new Date(candidateMillis + 24 * 60 * 60 * 1000));
  }
  return { date, startTime: preset.time };
};

const DemoKuliahComposer = ({ onSubmit, disabled = false }) => {
  const initialPreset = DEMO_KULIAH_PRESETS[0];
  const [form, setForm] = useState(() => ({
    presetId: initialPreset.id,
    speakerId: DEMO_SPEAKERS[0].id,
    kitabId: DEMO_BOOKS[0].id,
    topic: "",
    ...scheduleFor(initialPreset),
  }));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const selectedPreset = useMemo(
    () => DEMO_KULIAH_PRESETS.find(({ id }) => id === form.presetId) || initialPreset,
    [form.presetId, initialPreset]
  );

  const changeValue = (name, value) => {
    if (name === "presetId") {
      const preset = DEMO_KULIAH_PRESETS.find(({ id }) => id === value) || initialPreset;
      setForm((current) => ({ ...current, presetId: value, ...scheduleFor(preset) }));
    } else {
      setForm((current) => ({ ...current, [name]: value }));
    }
    setErrors({});
  };

  const change = (event) => {
    changeValue(event.target.name, event.target.value);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting || disabled) return;
    const speaker = DEMO_SPEAKERS.find(({ id }) => id === form.speakerId) || DEMO_SPEAKERS[0];
    const book = DEMO_BOOKS.find(({ id }) => id === form.kitabId) || DEMO_BOOKS[0];
    const value = {
      title: selectedPreset.title,
      speakerId: speaker.id,
      speakerName: speaker.name,
      speakerImage: speaker.image,
      kitabId: book.id,
      kitabTitle: book.title,
      topic: form.topic,
      date: form.date,
      startTime: form.startTime,
    };
    const validation = validateDemoKuliah(value);
    setErrors(validation.errors);
    if (!validation.valid) return;
    setSubmitting(true);
    try {
      await onSubmit(validation.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="demo-kuliah-composer" onSubmit={submit} noValidate>
      <p className="demo-eyebrow">ADMIN DEMO</p>
      <h2>Masukkan kuliah</h2>
      <div className="demo-field">
        <label htmlFor="demo-kuliah-preset">Jenis kuliah</label>
        <EtikafSelect
          id="demo-kuliah-preset"
          label="Jenis kuliah"
          value={form.presetId}
          options={DEMO_KULIAH_PRESETS.map((preset) => ({ value: preset.id, label: preset.title }))}
          disabled={disabled || submitting}
          onChange={(value) => changeValue("presetId", value)}
        />
      </div>
      <div className="demo-field">
        <label htmlFor="demo-kuliah-speaker">Ustaz</label>
        <DemoSpeakerSelect
          value={form.speakerId}
          disabled={disabled || submitting}
          onChange={(speakerId) => {
            setForm((current) => ({ ...current, speakerId }));
            setErrors({});
          }}
        />
      </div>
      <div className="demo-field">
        <label htmlFor="demo-kuliah-book">Kitab</label>
        <EtikafSelect
          id="demo-kuliah-book"
          label="Kitab"
          value={form.kitabId}
          options={DEMO_BOOKS.map((book) => ({ value: book.id, label: book.title }))}
          disabled={disabled || submitting}
          onChange={(value) => changeValue("kitabId", value)}
        />
      </div>
      <div className="demo-field">
        <label htmlFor="demo-kuliah-topic">Topik atau bab</label>
        <input id="demo-kuliah-topic" name="topic" value={form.topic} onChange={change} maxLength={160} />
        {errors.topic && <span className="demo-field-error">{errors.topic}</span>}
      </div>
      <div className="demo-kuliah-schedule">
        <div className="demo-field">
          <label htmlFor="demo-kuliah-date">Tarikh kuliah</label>
          <input id="demo-kuliah-date" name="date" type="date" value={form.date} onChange={change} />
        </div>
        <div className="demo-field">
          <label htmlFor="demo-kuliah-time">Masa kuliah</label>
          <input id="demo-kuliah-time" name="startTime" type="time" value={form.startTime} onChange={change} />
        </div>
      </div>
      {errors.dateTime && <p className="demo-field-error" role="alert">{errors.dateTime}</p>}
      <button type="submit" disabled={disabled || submitting}>
        {submitting ? "Menyimpan…" : "Paparkan kuliah"}
      </button>
    </form>
  );
};

export default DemoKuliahComposer;
